<template>
  <fts-client-modal
    append-to-body
    dynamic-screen
    width="1280px"
    :escape-event="!pinned"
    append-container-class="flow-diagram-modal-container"
    append-header-class="fd-head"
    append-content-class="fd-body"
    class="fts-flow-diagram-modal"
    @closed="reset"
    ref="ftsClientModal"
  >
    <!-- ledger head: module left, quiet facts right, one line -->
    <template #title>
      <div class="fd-title">
        <fts-moji :size="16" :emoji="diagramData.emoji" v-if="diagramData.emoji" />
        <span class="fd-title__name">{{ diagramData.name }}</span>
        <span class="fd-title__sep">·</span>
        <span class="fd-title__kind">Workflow</span>
      </div>
    </template>
    <template #actions>
      <div class="fd-facts" v-if="stateCount">
        <b>{{ stateCount }}</b> {{ stateCount === 1 ? 'state' : 'states' }}<i>·</i><b>{{ activityCount }}</b> {{ activityCount === 1 ? 'activity' : 'activities' }}
      </div>
    </template>
    <template #body>
      <div class="flow-diagram-body">
        <!-- workflow graph -->
        <div class="flow-diagram-graph" ref="graph">
          <fts-flow-diagram
            pinnable
            :diagram-data="diagramData"
            @element-hover="onElementHover"
            ref="diagram"
          />

          <!-- form card: what the hovered activity asks for, hung on its chip -->
          <div
            v-if="hover"
            class="fd-card"
            :class="{ 'is-pinned': hover.pinned, 'is-placed': !!cardPos }"
            :style="cardPos ? { left: cardPos.left + 'px', top: cardPos.top + 'px' } : null"
            ref="card"
          >
            <div class="fd-card__head">
              <div class="fd-card__name">
                <span v-if="hover.emoji">{{ hover.emoji }}</span>
                <span class="fd-card__name-text">{{ hover.name }}</span>
                <span class="fd-card__pin" v-if="hover.pinned">pinned · Esc</span>
              </div>
              <!-- one line per path: "Open, In Progress → Cancelled" -->
              <div class="fd-card__path" v-for="(tokens, p) in paths" :key="p">
                <template v-for="(token, i) in tokens">
                  <fts-state v-if="token.state" tint :state="token.state" class="fd-card__state" :key="i" />
                  <span v-else :class="'fd-card__' + token.type" :key="i">{{ token.text }}</span>
                </template>
                <span class="fd-card__tag" v-if="hoverConditional && p === paths.length - 1">conditional</span>
              </div>
            </div>
            <!-- the form — only when the diagram carries the module's information -->
            <template v-if="formAware">
              <template v-if="hoverFields.length">
                <div class="fd-card__ledger">
                  <span class="fd-card__cap">Form</span>
                  <span class="fd-card__facts">{{ formFacts }}</span>
                </div>
                <div class="fd-card__rows">
                  <div class="fd-card__row" v-for="(f, i) in shownFields" :key="i">
                    <i class="fd-card__ic" :class="f.icon"></i>
                    <span class="fd-card__fname">{{ f.label }}</span>
                    <span class="fd-card__fact fd-card__fact--req" v-if="f.required">required</span>
                    <span class="fd-card__fact" v-else-if="f.readOnly">read-only</span>
                  </div>
                </div>
                <div class="fd-card__more" v-if="hiddenFieldCount">
                  +{{ hiddenFieldCount }} more · click to pin
                </div>
              </template>
              <div v-else class="fd-card__empty">No form — this activity runs without input.</div>
            </template>
          </div>

          <!-- zoom -->
          <div class="fd-zoom" role="group" aria-label="Zoom">
            <button type="button" title="Zoom out" @click="zoom(1 / 1.2)">
              <font-awesome-icon :icon="['fas', 'minus']" />
            </button>
            <button type="button" title="Fit to view" @click="fit">Fit</button>
            <button type="button" title="Zoom in" @click="zoom(1.2)">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </button>
          </div>
          <div class="fd-hint">
            <template v-if="hotState">
              <template v-if="hotState.labels.length">From <b>{{ hotState.name }}</b>: {{ hotState.labels.join(' · ') }}</template>
              <template v-else><b>{{ hotState.name }}</b> is an end state — no activity leaves it</template>
            </template>
            <template v-else-if="formAware">Hover an activity to see its form · click to pin</template>
          </div>
        </div>

        <!-- side information list (only when the diagram carries an information list) -->
        <div class="flow-diagram-side" v-if="information.length">
          <div class="fd-side__head">
            <span class="fd-side__title">Information</span>
            <span class="fd-side__count">{{ information.length }}</span>
          </div>
          <div class="fd-side__hint">
            <template v-if="hoverActivityName">
              <template v-if="hasInvolved">Used by <strong>{{ hoverActivityName }}</strong> — {{ involvedNames.length }} of {{ information.length }}</template>
              <template v-else><strong>{{ hoverActivityName }}</strong> has no form</template>
            </template>
            <template v-else-if="hotInfo">
              <template v-if="hotInfoActivities.length"><strong>{{ hotInfo }}</strong> is filled in {{ hotInfoActivities.join(', ') }}</template>
              <template v-else><strong>{{ hotInfo }}</strong> is not on any form</template>
            </template>
            <template v-else>Hover an activity to see the information it uses — or a field to see where it is filled.</template>
          </div>
          <ul class="fd-side__list">
            <li
              v-for="(inf, i) in information"
              :key="i"
              class="fd-side__item"
              :class="{ 'is-involved': isLit(inf), 'is-dimmed': isDimmed(inf) }"
              :title="infoHint(inf)"
              @mouseenter="onInfoEnter(inf)"
              @mouseleave="onInfoLeave"
            >
              <i class="fd-side__ic" :class="infoMeta(inf).icon"></i>
              <span class="fd-side__name">{{ infoName(inf) }}</span>
              <span class="fd-side__type" :class="{ 'fd-side__type--req': involvedFact(inf) === 'required' }">
                {{ involvedFact(inf) || infoMeta(inf).label }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from 'vue';
import commons from '../commons/commons';
import flowDiagram from '../library/flowDiagram';
import FTSClientModal from './FTSClientModal';
import FTSFlowDiagram from './FTSFlowDiagram';
import FTSMoji from './FTSMoji';
import FTSState from './FTSState';

// a card lists this many fields before it folds the rest behind "+N more" — unless one
// more row would finish the list, or the card is pinned
const CARD_ROWS = 8;
const PSEUDO_STATES = { start: "start", any: "any state", change: "change status" };

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-moji": FTSMoji,
    "fts-state": FTSState
  },
  data: () => ({
    diagramData: {},
    // hovered / pinned activity — drives the form card and the side-list highlight:
    // { name, emoji, pinned, box, lit, flows } while an activity is lit, else null
    hover: null,
    hoverFields: [],    // resolved form fields: [{ key, label, icon, required, readOnly }]
    cardPos: null,      // { left, top } once the card has been measured and placed
    hotState: null,     // { name, labels } while a state is hovered
    hotInfo: ""         // name of the information row hovered in the side list
  }),
  computed: {
    information() {
      return Array.isArray(this.diagramData.information) ? this.diagramData.information : [];
    },
    // A diagram that lists the module's information knows its forms; one that does not (an
    // older backend) says nothing about them, so the card must not claim "no form".
    formAware() {
      return Array.isArray(this.diagramData.information);
    },
    stateCount() {
      return (this.diagramData.states || []).length;
    },
    activityCount() {
      return (this.diagramData.activities || []).length;
    },
    pinned() {
      return !!(this.hover && this.hover.pinned);
    },
    hoverActivityName() {
      return this.hover ? this.hover.name : "";
    },
    involvedNames() {
      return this.hoverFields.map((f) => f.key);
    },
    hasInvolved() {
      return this.involvedNames.length > 0;
    },
    shownFields() {
      const all = this.pinned || this.hoverFields.length <= CARD_ROWS + 1;
      return all ? this.hoverFields : this.hoverFields.slice(0, CARD_ROWS);
    },
    hiddenFieldCount() {
      return this.hoverFields.length - this.shownFields.length;
    },
    formFacts() {
      const count = this.hoverFields.length;
      const required = this.hoverFields.filter((f) => f.required).length;
      return `${count} field${count === 1 ? "" : "s"}` + (required ? ` · ${required} required` : "");
    },
    hoverConditional() {
      return !!this.hover && this.hover.flows.some((f) => f.conditional);
    },
    /**
     * The lit flows, one line per path: "Open, In Progress → Cancelled", or for a conditional
     * flow "Submitted → Approved / Rejected". States render as the card state chip.
     */
    paths() {
      if (!this.hover) return [];
      const conditional = this.hoverConditional;
      const groups = [];
      this.hover.flows.forEach((flow) => {
        const key = conditional ? flow.source : flow.target;
        const other = conditional ? flow.target : flow.source;
        let group = groups.find((x) => x.key === key);
        if (!group) groups.push(group = { key, others: [] });
        if (group.others.indexOf(other) === -1) group.others.push(other);
      });

      return groups.map((group) => {
        const tokens = [];
        const list = (ids, separator) => ids.forEach((id, i) => {
          if (i) tokens.push({ type: "sep", text: separator });
          tokens.push(this.stateToken(id));
        });
        if (conditional) {
          tokens.push(this.stateToken(group.key), { type: "arrow", text: "→" });
          list(group.others, "/");
        } else {
          list(group.others, ",");
          tokens.push({ type: "arrow", text: "→" }, this.stateToken(group.key));
        }
        return tokens;
      });
    },
    hotInfoActivities() {
      if (!this.hotInfo) return [];
      const key = this.hotInfo.toLowerCase();
      return (this.diagramData.activities || [])
        .filter((a) => flowDiagram.fieldNames(a).indexOf(key) !== -1)
        .map((a) => a.name);
    }
  },
  methods: {
    show(diagramData) {
      this.diagramData = diagramData;
      this.$refs.ftsClientModal.show();
      window.addEventListener("keydown", this.onKeydown);
    },
    /** Swap the diagram of an open modal (no-op once it has been closed). */
    update(diagramData) {
      const modal = this.$refs.ftsClientModal;
      if (modal && modal.showDialog) this.diagramData = diagramData;
    },
    /** Close the diagram if it's currently open (no-op otherwise). */
    hide() {
      const modal = this.$refs.ftsClientModal;
      if (modal && modal.showDialog) modal.close();
    },
    reset() {
      window.removeEventListener("keydown", this.onKeydown);
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    /**
     * React to FTSFlowDiagram's element-hover. Only activity edges (edges carrying a resolved
     * `activity`) raise the card; a state names what leaves it in the hint line; the synthetic
     * start/change edges just light up. An activity's `fields` are the information its form
     * holds — resolved here against the module's information list so the icons match the side
     * panel.
     */
    onElementHover(payload) {
      this.hotInfo = "";
      this.hotState = null;
      if (!payload || payload.group !== "edge" || !payload.data || !payload.data.activity) {
        this.clearHover();
        if (payload && payload.group === "node") {
          const labels = [];
          payload.flows.forEach((f) => { if (f.label && labels.indexOf(f.label) === -1) labels.push(f.label); });
          this.hotState = { name: payload.data.caption || payload.data.name, labels };
        }
        return;
      }
      const activity = payload.data.activity;
      const fields = Array.isArray(activity.fields) ? activity.fields : [];
      const resolved = [];
      fields.forEach((f) => {
        const name = this.fieldName(f);
        if (!name) return;
        const inf = this.findInformation(f, name);
        resolved.push({
          key: (this.infoName(inf) || name).toLowerCase(),
          label: this.infoName(inf) || name,
          icon: this.infoMeta(inf || { type: f && f.type }).icon,
          required: !!(f && f.required),
          readOnly: !!(f && f.readOnly)
        });
      });
      // a pan / zoom re-reports the same pinned activity: move the card, don't blink it
      const same = this.hover && this.hover.pinned && payload.pinned && this.hover.id === payload.hot.id;
      this.hoverFields = resolved;
      this.hover = {
        id: payload.hot.id,
        name: activity.name || "Activity",
        emoji: activity.emoji || "",
        pinned: payload.pinned,
        box: payload.box,
        lit: payload.lit,
        flows: payload.flows,
        x: payload.x,
        y: payload.y
      };
      if (!same) this.cardPos = null;
      this.$nextTick(this.placeCard);
    },
    /**
     * The card is measured after it renders, then hung on the activity's chip — beside it,
     * above or below, wherever it stays inside the graph and hides least of what is lit. All
     * boxes are in the (relatively-positioned) graph container's pixels, so the result is
     * unaffected by any transform on modal ancestors.
     */
    placeCard() {
      const card = this.$refs.card, graph = this.$refs.graph;
      if (!this.hover || !card || !graph) return;
      let anchor = this.hover.box;
      if (!anchor || !(anchor.width > 0)) {
        // no label box to hang on: fall back to where the pointer was
        const rect = graph.getBoundingClientRect();
        anchor = { left: (this.hover.x || 0) - rect.left, top: (this.hover.y || 0) - rect.top, width: 1, height: 1 };
      }
      this.cardPos = flowDiagram.placeCard({
        anchor,
        card: { width: card.offsetWidth, height: card.offsetHeight },
        container: { width: graph.clientWidth, height: graph.clientHeight },
        keepClear: this.hover.lit
      });
    },
    clearHover() {
      this.hover = null;
      this.hoverFields = [];
      this.cardPos = null;
    },
    onKeydown(e) {
      if (e.key === "Escape" && this.pinned && this.$refs.diagram) this.$refs.diagram.clearHighlight();
    },
    zoom(factor) {
      if (this.$refs.diagram) this.$refs.diagram.zoomBy(factor);
    },
    fit() {
      if (this.$refs.diagram) this.$refs.diagram.fit();
    },
    /* reverse lookup: a field lights the activities whose form holds it */
    onInfoEnter(inf) {
      if (this.pinned || !this.$refs.diagram) return;
      this.hotInfo = this.infoName(inf);
      this.$refs.diagram.highlightInformation(this.hotInfo);
    },
    onInfoLeave() {
      if (this.pinned || !this.$refs.diagram) return;
      this.hotInfo = "";
      this.$refs.diagram.highlightInformation(null);
    },
    stateToken(id) {
      const state = (this.diagramData.states || []).find((x) => String(x.id) === String(id));
      if (state) return { state: { name: state.name, color: state.color } };
      return { type: "pseudo", text: PSEUDO_STATES[id] || String(id) };
    },
    /* a field names its information by id (runtime) or by name (proposal schema) */
    findInformation(f, name) {
      const id = f && typeof f === "object" ? f.informationId : null;
      const key = name.toLowerCase();
      return this.information.find((inf) => id != null && inf && String(inf.id) === String(id))
        || this.information.find((inf) => this.infoName(inf).toLowerCase() === key);
    },
    fieldName(f) {
      if (f == null) return "";
      if (typeof f === "string") return f;
      return f.name || f.fieldName || f.displayName || "";
    },
    infoName(inf) {
      if (inf == null) return "";
      if (typeof inf === "string") return inf;
      return inf.displayName || inf.name || inf.fieldName || "";
    },
    infoHint(inf) {
      return (inf && (inf.ai_hint || inf.hint || inf.description)) || "";
    },
    isInvolved(inf) {
      const n = this.infoName(inf).toLowerCase();
      return !!n && this.involvedNames.indexOf(n) !== -1;
    },
    isLit(inf) {
      return this.isInvolved(inf) || (!!this.hotInfo && this.infoName(inf) === this.hotInfo);
    },
    isDimmed(inf) {
      return (this.hasInvolved || !!this.hotInfo) && !this.isLit(inf);
    },
    /* "required" / "read-only" for a field of the hovered activity's form, else "" */
    involvedFact(inf) {
      const n = this.infoName(inf).toLowerCase();
      const field = n && this.hoverFields.find((f) => f.key === n);
      if (!field) return "";
      return field.required ? "required" : field.readOnly ? "read-only" : "";
    },
    /**
     * Resolve a proposal field's type (string name or numeric id) to the app's information-type
     * icon + label. Mirrors FTSClientTeammate.fieldTypeMeta, incl. the design-schema type aliases,
     * so the side list and hover-card icons match the proposal card elsewhere in the chat.
     */
    infoMeta(f) {
      const t = f && f.type;
      const types = (commons && commons.informationTypes) || [];
      const ALIASES = {
        selection: "Select", currency: "Money", multitext: "Multiline Text", datetime: "Date Time",
        yesno: "Yes No", daterange: "Date Range", phone: "Phone Number", link: "URL"
      };
      const wanted = (t != null && ALIASES[String(t).toLowerCase()]) || t;
      const match = (wanted == null || wanted === "") ? null : types.find(
        (x) => String(x.id) === String(wanted) || (x.type && String(x.type).toLowerCase() === String(wanted).toLowerCase()));
      return {
        icon: (match && match.icon) || "fts-icon-text",
        label: (match && match.type) || (t != null ? String(t) : "")
      };
    }
  },
  beforeCreate() {
    Vue.component("fts-flow-diagram", FTSFlowDiagram);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
  }
}
</script>

<style lang="scss">
/* Calm density (SS05902): hairlines only, ground fills, no box-shadows. The form card has no
   shadow to lift it off the graph — it does not need one, the rest of the graph dims. */
.fts-flow-diagram-modal {
  --fd-brand-text: #1e6f8b;

  .flow-diagram-modal-container {
    height: 100%;
  }
  // ledger head — one 44px line
  .client-modal__header.fd-head {
    align-items: center;
    height: 44px;
    padding: 0 0.5rem 0 1rem;
    .client-modal-header {
      align-items: center;
    }
  }
  .fd-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: var(--fts-text);
  }
  .fd-title__name {
    font-weight: 600;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .fd-title__sep {
    color: var(--fts-gray-400);
  }
  .fd-title__kind {
    font-size: 12.5px;
    color: var(--fts-gray-500);
  }
  .fd-facts {
    font-size: 11px;
    color: var(--fts-gray-400);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    padding-right: 6px;
    b { font-weight: 500; color: var(--fts-gray-500); }
    i { font-style: normal; color: var(--fts-border); padding: 0 5px; }
  }
  // the graph runs edge to edge, so the side list's hairline meets the head's
  .client-modal__body.fd-body {
    padding: 0;
  }
  .flow-diagram-body {
    display: flex;
    height: 100%;
    width: 100%;
  }
  .flow-diagram-graph {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    overflow: hidden;
  }
  .flow-diagram-side {
    flex: 0 0 236px;
    width: 236px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
  }
  .fd-side__head {
    display: flex;
    align-items: baseline;
    padding: 12px 14px 2px;
  }
  .fd-side__title {
    flex: 1;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--fts-gray-400);
  }
  .fd-side__count {
    font-size: 11px;
    color: var(--fts-gray-400);
    font-variant-numeric: tabular-nums;
  }
  .fd-side__hint {
    min-height: 40px;
    font-size: 11px;
    color: var(--fts-gray-400);
    padding: 0 14px 8px;
    border-bottom: 1px solid var(--fts-gray-100);
    strong { font-weight: 500; color: var(--fts-text); }
  }
  .fd-side__list {
    list-style: none;
    margin: 0;
    padding: 6px 8px 10px;
    overflow-y: auto;
    flex: 1 1 auto;
  }
  .fd-side__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 27px;
    padding: 0 6px;
    border-radius: 5px;
    cursor: default;
    transition: opacity .12s ease, background-color .12s ease;
    .fd-side__ic { color: var(--fts-gray-400); font-size: 13px; width: 16px; text-align: center; flex: 0 0 auto; }
    .fd-side__name { font-size: 12px; color: var(--fts-text); flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .fd-side__type { font-size: 10.5px; color: var(--fts-gray-400); flex: 0 0 auto; }
    .fd-side__type--req { color: var(--fts-formula-ref, #b26a00); }
    &:hover { background-color: var(--fts-gray-100); }
    &.is-involved {
      background-color: var(--fts-primary-hover);
      .fd-side__ic, .fd-side__name { color: var(--fd-brand-text); }
      .fd-side__name { font-weight: 500; }
    }
    &.is-dimmed { opacity: .34; }
  }
  .fd-card {
    position: absolute;
    z-index: 5;
    // measured off-screen first, then hung on the chip (see placeCard)
    left: 0;
    top: 0;
    visibility: hidden;
    pointer-events: none;
    width: 252px;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    &.is-placed { visibility: visible; }
    &.is-pinned {
      pointer-events: auto;
      border-color: rgba(var(--fts-primary-rgb), .45);
      .fd-card__rows { max-height: 292px; overflow-y: auto; }
    }
  }
  .fd-card__head {
    padding: 9px 12px 8px;
  }
  .fd-card__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 12.5px;
    color: var(--fts-text);
  }
  .fd-card__name-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .fd-card__pin {
    margin-left: auto;
    flex: 0 0 auto;
    font-weight: 400;
    font-size: 10px;
    color: var(--fts-gray-400);
  }
  .fd-card__path {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px 5px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--fts-gray-500);
    // the card state chip, a size down to sit in a ledger line
    .fd-card__state > div {
      padding: 0 5px;
      font-size: 11px;
      line-height: 17px;
    }
  }
  .fd-card__arrow, .fd-card__sep {
    color: var(--fts-gray-400);
  }
  .fd-card__tag {
    font-size: 10px;
    color: var(--fts-gray-400);
    border: 1px dashed var(--fts-border);
    border-radius: 4px;
    padding: 0 4px;
  }
  .fd-card__ledger {
    display: flex;
    align-items: baseline;
    padding: 8px 12px 3px;
    border-top: 1px solid var(--fts-gray-100);
  }
  .fd-card__cap {
    flex: 1;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--fts-gray-400);
  }
  .fd-card__facts {
    font-size: 10.5px;
    color: var(--fts-gray-400);
    font-variant-numeric: tabular-nums;
  }
  .fd-card__rows {
    padding: 0 6px 6px;
  }
  .fd-card__row {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 25px;
    padding: 0 6px;
    .fd-card__ic { color: var(--fts-gray-400); font-size: 12px; width: 14px; text-align: center; flex: 0 0 auto; }
    .fd-card__fname { font-size: 12px; color: var(--fts-text); flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .fd-card__fact { font-size: 10.5px; color: var(--fts-gray-400); flex: 0 0 auto; }
    .fd-card__fact--req { color: var(--fts-formula-ref, #b26a00); }
  }
  .fd-card__more {
    font-size: 10.5px;
    color: var(--fts-gray-400);
    padding: 0 12px 9px;
  }
  .fd-card__empty {
    font-size: 11.5px;
    color: var(--fts-gray-500);
    padding: 9px 12px 10px;
    border-top: 1px solid var(--fts-gray-100);
  }
  .fd-zoom {
    position: absolute;
    left: 12px;
    bottom: 12px;
    display: inline-flex;
    overflow: hidden;
    border-radius: 6px;
    border: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    button {
      min-width: 28px;
      height: 26px;
      padding: 0 8px;
      border: 0;
      background: none;
      font-size: 11.5px;
      color: var(--fts-gray-500);
      cursor: pointer;
      + button { border-left: 1px solid var(--fts-border); }
      &:hover { background-color: var(--fts-gray-100); color: var(--fts-text); }
      &:focus-visible { outline: 2px solid var(--fts-primary); outline-offset: -2px; }
      svg { font-size: 10px; }
    }
  }
  .fd-hint {
    position: absolute;
    right: 14px;
    bottom: 16px;
    max-width: 62%;
    text-align: right;
    font-size: 11px;
    color: var(--fts-gray-400);
    pointer-events: none;
    b { font-weight: 500; color: var(--fts-gray-500); }
  }
}

body.dark .fts-flow-diagram-modal {
  --fd-brand-text: #7cc8e3;
}
</style>
